import { skipToken, useQuery } from '@tanstack/react-query';
import { Command } from 'cmdk';
import {
  CalendarCheck, CheckCircle2, Clock, Eye, FileUp, Keyboard, Layers, LifeBuoy, Moon, Plus, Receipt, Search,
} from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import type { Paginated, TransactionRow } from '@/api/contract';
import { useTRPC } from '@/api/query';
import { Kbd, MoneyText } from '@/components/ui';
import { useDates } from '@/lib/dates';
import { keyLabels, useHotkeys } from '@/lib/keyboard';
import { usePreferences, useSession } from '@/lib/session';
import { useAppStore } from '@/lib/store';
import { SETTINGS_ITEM } from './nav';
import { useNavItems } from './useNavItems';
import { loadHelpIndex } from '@/help';

const useDebounced = <T,>(value: T, ms = 200): T => {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
};

const groupHeading =
  '[&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:pt-3 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-text-muted';

const Item = ({ onSelect, icon, children, keys, value, forceMount, keywords }: {
  readonly onSelect: () => void; readonly icon: ReactNode; readonly children: ReactNode; readonly keys?: string; readonly value: string; readonly forceMount?: boolean;
  readonly keywords?: string[];
}) => (
  <Command.Item
    value={value}
    keywords={keywords}
    onSelect={onSelect}
    forceMount={forceMount}
    className="flex min-h-11 cursor-pointer items-center gap-3 rounded-md px-3 text-sm text-text data-[selected=true]:bg-primary-soft [&_svg]:size-4 [&_svg]:shrink-0 [&_svg]:text-text-muted"
  >
    {icon}
    <span className="min-w-0 flex-1 truncate">{children}</span>
    {keys ? (
      <span className="flex gap-1" aria-hidden>
        {keyLabels(keys).map((step, i) => (
          <span key={i} className="flex gap-0.5">
            {step.map((k) => (
              <Kbd key={k}>{k}</Kbd>
            ))}
          </span>
        ))}
      </span>
    ) : null}
  </Command.Item>
);

/**
 * ⌘K command palette: navigate, actions, switch entity, toggles, recent pages and transaction search
 * (`transactions.list({ q })`). Also opens with `/`. Owns its own hotkeys.
 */
export const CommandPalette = () => {
  const open = useAppStore((s) => s.paletteOpen);
  const set = useAppStore((s) => s.set);
  const setEntity = useAppStore((s) => s.setEntity);
  const recent = useAppStore((s) => s.recentPages);
  const navItems = useNavItems();
  const helpIndex = useQuery({ queryKey: ['help-index'], queryFn: loadHelpIndex, enabled: open, staleTime: Infinity });
  const openHelp = useAppStore((s) => s.openHelp);
  const navigate = useNavigate();
  const trpc = useTRPC();
  const dates = useDates();
  const { entityId, memberships } = useSession();
  const { privacyBlur, theme, setPreference } = usePreferences();
  const [query, setQuery] = useState('');
  const q = useDebounced(query.trim());

  useHotkeys({
    'mod+k': () => set({ paletteOpen: !useAppStore.getState().paletteOpen }),
    '/': () => set({ paletteOpen: true }),
  });

  const search = useQuery(
    trpc.transactions.list.queryOptions(open && entityId && q.length >= 2 ? { entityId, q, limit: 8 } : skipToken, { meta: { silent: true } }),
  );
  const results: Paginated<TransactionRow> | undefined = search.data;

  const close = () => {
    set({ paletteOpen: false });
    setQuery('');
  };
  const run = (fn: () => void) => () => {
    close();
    fn();
  };

  return (
    <Command.Dialog
      open={open}
      onOpenChange={(o) => (o ? set({ paletteOpen: true }) : close())}
      label="Command palette"
      loop
      overlayClassName="fixed inset-0 z-50 bg-text/40 backdrop-blur-[2px]"
      contentClassName="fixed left-1/2 top-[12vh] z-50 w-[calc(100vw-2rem)] max-w-xl -translate-x-1/2 overflow-hidden rounded-xl border border-border bg-surface-raised shadow-lg animate-scale-in"
    >
      <div className="flex items-center gap-2 border-b border-border px-4">
        <Search className="size-4 text-text-muted" aria-hidden />
        <Command.Input
          value={query}
          onValueChange={setQuery}
          placeholder="Search pages, actions and transactions…"
          className="h-12 w-full bg-transparent text-sm text-text outline-none placeholder:text-text-muted"
        />
        <Kbd aria-hidden>Esc</Kbd>
      </div>
      <Command.List className={`max-h-[60vh] overflow-y-auto p-2 ${groupHeading}`}>
        <Command.Empty className="px-3 py-8 text-center text-sm text-text-muted">
          {search.isFetching ? 'Searching…' : 'No matches. Try a payee, amount or page name.'}
        </Command.Empty>

        {results?.items.length ? (
          <Command.Group heading="Transactions" forceMount>
            {results.items.map((t) => (
              <Item
                key={t.id}
                value={`txn ${t.id} ${q}`}
                forceMount
                icon={<Receipt aria-hidden />}
                onSelect={run(() => navigate(`/transactions?focus=${encodeURIComponent(t.id)}`))}
              >
                <span className="flex items-center gap-3">
                  <span className="min-w-0 flex-1 truncate">{t.payeeRaw || t.memo || 'Transaction'}</span>
                  <span className="text-xs text-text-muted">{dates.format(t.date, 'dayMonth')}</span>
                  <MoneyText cents={t.amountCents} tone="auto" className="text-sm" />
                </span>
              </Item>
            ))}
          </Command.Group>
        ) : null}

        {!query && recent.length ? (
          <Command.Group heading="Recent">
            {recent.map((p) => (
              <Item key={p.path} value={`recent ${p.title} ${p.path}`} icon={<Clock aria-hidden />} onSelect={run(() => navigate(p.path))}>
                {p.title}
              </Item>
            ))}
          </Command.Group>
        ) : null}

        <Command.Group heading="Actions">
          <Item value="New transaction add" keys="n" icon={<Plus aria-hidden />} onSelect={run(() => navigate('/transactions?new=1'))}>
            New transaction
          </Item>
          <Item value="Import file bank csv ofx qif" icon={<FileUp aria-hidden />} onSelect={run(() => navigate('/transactions?import=1'))}>
            Import a file
          </Item>
          <Item value="Daily check-in review" icon={<CheckCircle2 aria-hidden />} onSelect={run(() => navigate('/checkin'))}>
            Start daily check-in
          </Item>
          <Item value="Weekly review reflect" icon={<CalendarCheck aria-hidden />} onSelect={run(() => navigate('/review/weekly'))}>
            Weekly review
          </Item>
          <Item value="Toggle privacy blur hide amounts" icon={<Eye aria-hidden />} onSelect={run(() => setPreference('privacyBlur', !privacyBlur))}>
            {privacyBlur ? 'Show amounts' : 'Hide amounts (privacy blur)'}
          </Item>
          <Item
            value="Toggle theme dark light mode"
            icon={<Moon aria-hidden />}
            onSelect={run(() => setPreference('theme', theme === 'dark' ? 'light' : 'dark'))}
          >
            Switch to {theme === 'dark' ? 'light' : 'dark'} theme
          </Item>
          <Item value="Help for this page" keys="?" icon={<LifeBuoy aria-hidden />} onSelect={run(() => openHelp(null))}>
            Help for this page
          </Item>
          <Item value="Keyboard shortcuts" icon={<Keyboard aria-hidden />} onSelect={run(() => set({ shortcutsOpen: true }))}>
            Keyboard shortcuts
          </Item>
        </Command.Group>

        <Command.Group heading="Go to">
          {[...navItems, SETTINGS_ITEM].map((item) => {
            const Icon = item.icon;
            return (
              <Item key={item.id} value={`Go to ${item.label}`} keys={item.keys} icon={<Icon aria-hidden />} onSelect={run(() => navigate(item.to))}>
                Go to {item.label}
              </Item>
            );
          })}
        </Command.Group>

        {query.trim().length > 1 && helpIndex.data?.length ? (
          // help articles by title and their words ("new computer" → Moving to a new computer)
          <Command.Group heading="Help articles">
            {helpIndex.data.map((h) => (
              <Item key={h.key} value={`Help ${h.title}`} keywords={h.keywords} icon={<LifeBuoy aria-hidden />} onSelect={run(() => openHelp(h.key))}>
                {h.title}
              </Item>
            ))}
          </Command.Group>
        ) : null}

        {memberships.length > 1 ? (
          <Command.Group heading="Switch entity">
            {memberships.map((m) => (
              <Item key={m.entityId} value={`Switch entity ${m.entity.name}`} icon={<Layers aria-hidden />} onSelect={run(() => setEntity(m.entityId))}>
                Switch to {m.entity.name}
              </Item>
            ))}
            <Item value="Switch entity consolidated all" icon={<Layers aria-hidden />} onSelect={run(() => set({ consolidated: true }))}>
              Consolidated view
            </Item>
          </Command.Group>
        ) : null}
      </Command.List>
    </Command.Dialog>
  );
};
